import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import type { RoleName, TenantType } from '@/types';

interface Props {
  /** If set, user must have at least one of these roles. */
  requireAny?: RoleName[];
  /**
   * If set, user's tenant must be one of these types. Role assignment
   * (`requireAny`) is independent of tenant type in this system — every
   * tenant's admin user gets only TENANT_ADMIN regardless of whether the
   * tenant is a wholesaler, retailer, or rep — so role checks alone don't
   * stop a retailer/rep from reaching a wholesaler-only screen (or vice
   * versa). This checks the actual tenant type instead.
   */
  requireTenantType?: TenantType[];
}

/**
 * Auth gate. Used as a layout route — wraps an <Outlet /> so children
 * render only when the user is authenticated (and optionally has the
 * required role / belongs to an allowed tenant type).
 */
export function ProtectedRoute({ requireAny, requireTenantType }: Props) {
  const { isAuthenticated, hasAnyRole, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requireAny && !hasAnyRole(requireAny)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireTenantType && !requireTenantType.includes(user?.tenantType as TenantType)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
}
